import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { getNotifications, markNotificationsRead, saveNotificationPrefs } from '../services/api';
import { fmtDateTime } from '../utils/kernelFormat';
import './kernelNotificationBell.css';
import './kernelSwitch.css';

const POLL_MS = 60000;

/**
 * Campanello delle notifiche: pulsante fisso in basso a destra con il numero di notifiche non lette, che si aggiorna
 * da solo ogni minuto. Da qui ognuno sceglie anche quali notifiche ricevere e se riceverle via email.
 * I tipi di notifica li dichiara l'app (Hooks::notificationTypes); la scritta di ciascuno sta nei testi
 * dell'app come "notifications.type_<tipo>" (se manca si mostra il nome del tipo).
 */
function KernelNotificationBell() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread: 0, prefs: { email_enabled: true, disabled_types: [] }, types: [] });
  const [showPrefs, setShowPrefs] = useState(false);
  const [available, setAvailable] = useState(true);
  const rootRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await getNotifications();
      setAvailable(res.available !== false);
      setData(res);
    } catch (err) {
      // il campanello è accessorio: se non risponde si riprova al prossimo giro
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.is_read) {
      markNotificationsRead({ ids: [n.id] }).then(load).catch(() => {});
    }
    if (n.link) navigate(n.link, { state: { from: window.location.pathname } });
  };

  const markAll = async () => {
    await markNotificationsRead({ all: true }).catch(() => {});
    load();
  };

  const togglePref = async (key, value) => {
    const prefs = { ...data.prefs };
    if (key === 'email') {
      prefs.email_enabled = value;
    } else {
      const disabled = new Set(prefs.disabled_types);
      if (value) disabled.delete(key);
      else disabled.add(key);
      prefs.disabled_types = Array.from(disabled);
    }
    setData({ ...data, prefs });
    try {
      await saveNotificationPrefs({ email_enabled: prefs.email_enabled, disabled_types: prefs.disabled_types });
    } catch (err) {
      load();
    }
  };

  const typeLabel = (ty) => {
    const key = `notifications.type_${ty}`;
    const label = t(key);
    return label === key ? ty : label;
  };

  if (!available) return null;

  return (
    <div className="nb-root" ref={rootRef}>
      {open && (
        <div className="nb-panel" role="dialog" aria-label={t('notifications.title')}>
          <div className="nb-head">
            <strong>{t('notifications.title')}</strong>
            <span className="nb-spacer" />
            {data.unread > 0 && <button type="button" className="nb-link" onClick={markAll}>{t('notifications.markAll')}</button>}
            <button type="button" className="nb-link" onClick={() => setShowPrefs((v) => !v)}>{t('notifications.settings')}</button>
          </div>

          {showPrefs && (
            <div className="nb-prefs">
              <label className="ksw-row">
                <span className="ksw"><input type="checkbox" checked={data.prefs.email_enabled} onChange={(e) => togglePref('email', e.target.checked)} /><span className="ksw-slider" /></span>
                <span>{t('notifications.emailToo')}</span>
              </label>
              <p className="nb-hint">{t('notifications.typesHint')}</p>
              {data.types.map((ty) => (
                <label key={ty} className="ksw-row">
                  <span className="ksw"><input type="checkbox" checked={!data.prefs.disabled_types.includes(ty)} onChange={(e) => togglePref(ty, e.target.checked)} /><span className="ksw-slider" /></span>
                  <span>{typeLabel(ty)}</span>
                </label>
              ))}
            </div>
          )}

          {data.notifications.length === 0 ? (
            <p className="nb-empty">{t('notifications.empty')}</p>
          ) : (
            <ul className="nb-list">
              {data.notifications.map((n) => (
                <li key={n.id}>
                  <button type="button" className={'nb-item' + (n.is_read ? '' : ' nb-item-unread')} onClick={() => openItem(n)}>
                    <span className="nb-item-dot" aria-hidden="true" />
                    <span className="nb-item-text">
                      <span className="nb-item-title">{n.title}</span>
                      {n.body && <span className="nb-item-body">{n.body}</span>}
                      <span className="nb-item-time">{fmtDateTime(n.created_at)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <button type="button" className="nb-fab" onClick={() => setOpen((v) => !v)} aria-label={t('notifications.title')} title={t('notifications.title')}>
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {data.unread > 0 && <span className="nb-badge">{data.unread > 99 ? '99+' : data.unread}</span>}
      </button>
    </div>
  );
}

export default KernelNotificationBell;
